import { Content } from '@radix-ui/react-dialog'

import { Button, cn, VisuallyHidden } from '@seen/ui'
import { IconX } from '@tabler/icons-react'
import {
  type ComponentPropsWithoutRef,
  type ElementRef,
  forwardRef,
} from 'react'
import { DialogClose } from '#src/components/core/dialog/DialogClose.tsx'
import { DialogTitle } from '#src/components/core/dialog/DialogTitle.tsx'
import { DialogOverlay } from '#src/components/core/dialog/display/DialogOverlay.tsx'
import { DialogPortal } from '#src/components/core/dialog/display/DialogPortal.tsx'
import { ScrollArea } from '#src/components/core/scroll-area/ScrollArea.tsx'

export type ContentProps = ComponentPropsWithoutRef<typeof Content>

export interface DialogDisplayProps extends ContentProps {
  forceMount?: ContentProps['forceMount']
  onOpenAutoFocus?: ContentProps['onOpenAutoFocus']
  onCloseAutoFocus?: ContentProps['onCloseAutoFocus']
  onEscapeKeyDown?: ContentProps['onEscapeKeyDown']
  onPointerDownOutside?: ContentProps['onPointerDownOutside']
  onInteractOutside?: ContentProps['onInteractOutside']
  notScrollable?: boolean
  hideCloseButton?: boolean
  children?: ContentProps['children']
  asChild?: ContentProps['asChild']
  className?: ContentProps['className']
  scrollAreaContentClassName?: string
  // Overrides the backdrop scrim styling. Used by stacked dialogs to lighten the
  // scrim so parent layers stay visible behind the active one.
  overlayClassName?: string
  size?: 'default' | 'lg' | 'xl'
}

export const DialogDisplay = forwardRef<
  ElementRef<typeof Content>,
  DialogDisplayProps
>(
  (
    {
      children,
      className,
      scrollAreaContentClassName,
      overlayClassName,
      hideCloseButton,
      notScrollable,
      size = 'default',
      ...delegated
    },
    ref
  ) => (
    <DialogPortal>
      <DialogOverlay className={overlayClassName}>
        <Content
          ref={ref}
          aria-describedby={undefined}
          className={cn(
            'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 relative isolate z-portal mx-auto mt-[13dvh] grid max-h-[74dvh] w-full auto-rows-fr overflow-hidden border-border border-t w-2xl:border-r border-b w-2xl:border-l bg-background text-foreground shadow-lg ring-offset-background duration-200 data-[state=closed]:animate-out data-[state=open]:animate-in sm:rounded-lg',
            {
              'max-w-3xl': size === 'default',
              'max-w-5xl': size === 'lg',
              'max-w-7xl': size === 'xl',
            },
            className
          )}
          {...delegated}
        >
          <VisuallyHidden>
            <DialogTitle>Dialog</DialogTitle>
          </VisuallyHidden>
          {notScrollable && children}
          {!notScrollable && (
            <ScrollArea
              contentClassName={scrollAreaContentClassName}
              scrollRestorationId={null}
            >
              {children}
            </ScrollArea>
          )}
          {hideCloseButton && (
            <VisuallyHidden>
              <DialogClose />
            </VisuallyHidden>
          )}
          {!hideCloseButton && (
            <DialogClose asChild>
              <Button
                size="icon"
                variant="ghost"
                className={cn('absolute top-3 right-3 text-muted-foreground')}
              >
                <IconX className="size-4" />
              </Button>
            </DialogClose>
          )}
        </Content>
      </DialogOverlay>
    </DialogPortal>
  )
)
DialogDisplay.displayName = Content.displayName
